<template>
  <kv-card
    v-if="item"
    class="linked-commerce-panel"
    collapsible
    variant="soft"
    density="compact"
    v-model="isOpen"
  >
    <template #title>
      <div class="linked-commerce-title" @click.stop="isOpen = !isOpen">
        <kv-typography weight="medium" color="secondary" size="medium">
          {{ panelTitle }}
        </kv-typography>
      </div>
    </template>

    <template #subtitle>
      <div class="linked-commerce-subtitle">
        <div class="linked-commerce-meta">
          <kv-chip
            v-if="statusChip"
            :color="statusChip.color"
            density="extra-compact"
            rounded="soft"
          >
            {{ statusChip.label }}
          </kv-chip>
          <human-date
            v-if="itemDate"
            :date="itemDate"
            format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf"
          />
          <template v-if="item.branch">
            <Icon icon-class="ui-marker-pin-02" size="2" color="#209941" />
            <!-- i18n-validator-ignore-next-line: runtime branch name -->
            <span>{{ item.branch }}</span>
          </template>
        </div>
        <kv-typography size="small" weight="medium">
          {{ valueLabel }} {{ formatCurrency(item.value) }}
          <template
            v-if="
              item.original_currency &&
              item.original_currency !== placeCurrency
            "
          >
            ({{ formatCurrency(item.original_value, item.original_currency) }})
          </template>
        </kv-typography>
      </div>
    </template>

    <template #text>
      <div class="linked-commerce-body">
        <div v-if="products.length" class="linked-commerce-products">
          <kv-typography color="secondary" size="small">
            {{ t("clients-detail-products") }}
          </kv-typography>
          <ul class="list">
            <li v-for="(product, index) in products" :key="product.id || index">
              <kv-typography color="secondary">
                {{ formatQuantity(product.quantity) }}
                <!-- i18n-validator-ignore-next-line: runtime product name -->
                {{ product.name }}
              </kv-typography>
            </li>
          </ul>
        </div>

        <kv-chip
          v-if="type === 'order' && item.is_delivery === '1'"
          rounded="soft"
          density="compact"
        >
          {{ t("clients-detail-delivery") }}
        </kv-chip>

        <div class="linked-commerce-actions" v-if="clientProfileRoute">
          <router-link class="open-commerce-link" :to="clientProfileRoute">
            {{ t("tasks-view_on_client") }}
            <Icon icon-class="ui-chevron-right" size="2" />
          </router-link>
        </div>
      </div>
    </template>
  </kv-card>
</template>

<script>
import KvCard from "../ui/kv-card.vue";
import KvChip from "../ui/kv-chip.vue";
import KvTypography from "../ui/kv-typography.vue";
import Icon from "../icon/icon";
import HumanDate from "../human-date/human-date.vue";
import currencyMixin from "../../mixins/currency-mixin";

export default {
  name: "LinkedCommercePanel",
  mixins: [currencyMixin],
  components: {
    KvCard,
    KvChip,
    KvTypography,
    Icon,
    HumanDate,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ["order", "quotation"].includes(value),
    },
    item: {
      type: Object,
      default: null,
    },
    defaultOpen: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isOpen: this.defaultOpen,
    };
  },
  computed: {
    panelTitle() {
      return this.type === "order"
        ? this.t("tasks-order")
        : this.t("tasks-quotation");
    },
    valueLabel() {
      return this.type === "order"
        ? this.t("clients-detail-made_order_of")
        : this.t("clients-detail-has_quotation_of");
    },
    itemDate() {
      if (!this.item) return null;
      return this.item.created || this.item.created_at || this.item.time || null;
    },
    products() {
      if (!this.item || !Array.isArray(this.item.products)) {
        return [];
      }
      return this.item.products;
    },
    statusChip() {
      if (!this.item) return null;
      if (this.type === "order") {
        if (this.item.status === "cancelled") {
          return {
            label: this.t("clients-detail-order_cancelled"),
            color: "error",
          };
        }
        return {
          label: this.t("clients-timeline-order_created"),
          color: "success",
        };
      }
      if (["expired", "rejected"].includes(this.item.status)) {
        const statusLabel =
          this.item.status === "expired"
            ? this.t("clients-detail-quotation_status_expired")
            : this.t("clients-detail-quotation_status_rejected");
        return {
          label: `${this.t("clients-detail-quotation")} ${statusLabel}`,
          color: "error",
        };
      }
      if (this.item.status === "accepted") {
        return {
          label: this.t("clients-detail-status_accepted"),
          color: "success",
        };
      }
      if (this.item.status === "sent") {
        return {
          label: this.t("clients-detail-status_sent"),
          color: "default",
        };
      }
      return null;
    },
    clientProfileRoute() {
      if (!this.item || !this.item.clients_id || !this.item.id) {
        return null;
      }
      return {
        path: `/client/${this.item.clients_id}`,
        hash: `#${this.item.id}`,
      };
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    formatQuantity(quantity) {
      return Math.round(Number(quantity || 0) * 100) / 100;
    },
  },
};
</script>

<style scoped lang="less">
.linked-commerce-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  cursor: pointer;
  padding-right: 28px;
}

.linked-commerce-subtitle {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.linked-commerce-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: #6c737f;
}

.linked-commerce-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
}

.linked-commerce-products {
  width: 100%;

  .list {
    margin: 6px 0 0;
    padding-left: 18px;
  }
}

.linked-commerce-actions {
  width: 100%;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
}

.open-commerce-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--primary);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
